import { ALLOWED_IMAGE_CONTENT_TYPES } from '@repo/api-contracts';
import { ImagePlus } from 'lucide-react';
import { type ReactElement, useId, useRef } from 'react';
import { RailIconButton } from '@/shared/ui';
import { type UploadedDesignImage, useUploadDesignImage } from '../model/use-upload-design-image';

interface AddImageButtonProps {
  readonly disabledReason?: string | null;
  /** 비회원 키(G6) — 있으면 비회원 업로드 API 로 올린다. 회원은 null. */
  readonly guestKey?: string | null;
  readonly onUploaded: (image: UploadedDesignImage) => void;
}

/**
 * 스테이지 도구 칸의 [이미지 업로드] — 파일 선택 → 업로드 → 디자인에 추가.
 * 인쇄 영역이 없는 면 등 비활성 사유가 있으면 버튼을 막고 사유를 알린다. 업로드 오류는 칸 옆(좁은 화면은 아래)에 붉은 줄로 보인다.
 */
export const AddImageButton = ({
  disabledReason = null,
  guestKey = null,
  onUploaded,
}: AddImageButtonProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const reasonId = useId();
  const { upload, isUploading, error, clearError } = useUploadDesignImage({ guestKey });
  const disabled = disabledReason !== null || isUploading;

  return (
    <div className="relative flex min-w-0 flex-1 lg:flex-none">
      <RailIconButton
        icon={ImagePlus}
        label={isUploading ? '올리는 중…' : '이미지 업로드'}
        title={disabledReason ?? '이미지 업로드 (PNG·JPG·WebP, 10MB 이하)'}
        describedBy={disabledReason === null ? undefined : reasonId}
        disabled={disabled}
        busy={isUploading}
        onClick={() => {
          clearError();
          inputRef.current?.click();
        }}
      />
      <input
        ref={inputRef}
        aria-label="이미지 파일 선택"
        type="file"
        tabIndex={-1}
        disabled={disabled}
        accept={ALLOWED_IMAGE_CONTENT_TYPES.join(',')}
        className="sr-only"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file === undefined) return;
          const uploaded = await upload(file);
          if (uploaded !== null) onUploaded(uploaded);
        }}
      />
      {disabledReason !== null && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
      {error !== null && (
        <p
          role="alert"
          className="absolute top-0 left-full z-20 w-56 bg-destructive px-3 py-2 text-xs font-medium text-white shadow-md max-lg:top-full max-lg:left-0"
        >
          {error}
        </p>
      )}
    </div>
  );
};
